/**
 * Pack list editor component for plan creation/editing.
 *
 * Manages two separate gear lists (team and individual) with:
 * - Inline editing (name + quantity always visible)
 * - Note toggle (description textarea, like timeline notes)
 * - Grouping (by template or custom group)
 * - Add from template (per-section)
 * - Add custom group
 * - Quick-add per group
 */

import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import type { PlanPackItem } from '../../schemas/plan.js'
import { DEFAULT_PACK_TEMPLATES, type PackTemplateData } from '../../data/default-pack-templates.js'
import { groupPackItems, type PackGroup } from '../../utils/pack-groups.js'
import { packListStorage, packItemStorage } from '../../storage/pack-storage.js'
import '@material/web/iconbutton/icon-button.js'
import '@material/web/textfield/outlined-text-field.js'
import '@material/web/button/outlined-button.js'
import '@material/web/button/text-button.js'
import { authService } from '../../services/auth/auth-service.js'

@customElement('plan-pack-editor')
export class PlanPackEditor extends LitElement {
  static override styles = css`
    :host {
      display: block;
    }

    .subsection-label {
      font-size: 13px;
      font-weight: 600;
      color: var(--cairn-text-secondary-color, #64748b);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: var(--cairn-spacing-xs, 4px);
    }

    /* Group container */
    .gear-group {
      margin-bottom: var(--cairn-spacing-sm, 8px);
    }

    .gear-group-header {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px var(--cairn-spacing-sm, 8px);
      background: var(--cairn-surface-color, #ffffff);
      border-radius: 6px 6px 0 0;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-bottom: none;
    }

    .gear-group-name {
      flex: 1;
      font-size: 13px;
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
    }

    .gear-group-remove,
    .gear-group-save {
      flex-shrink: 0;
      --md-icon-button-icon-color: var(--cairn-text-secondary-color, #64748b);
    }

    .gear-group-saved {
      font-size: 12px;
      color: var(--cairn-primary-color, #3b82f6);
      padding: 0 4px;
    }

    /* Item wrapper — matches time-entry-wrapper */
    .gear-item-wrapper {
      position: relative;
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-bottom: none;
      transition:
        border-color 0.15s,
        opacity 0.15s;
    }

    .gear-item-wrapper:last-of-type {
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .gear-item-wrapper.dragging {
      opacity: 0.4;
      cursor: grabbing;
    }

    .gear-item-wrapper.drag-over {
      border-top: 2px solid var(--cairn-primary-color, #3b82f6);
    }

    /* Drop zone at bottom of group */
    .gear-group-drop-zone {
      height: 0;
      transition: height 0.15s;
    }

    .gear-group-drop-zone.drag-over {
      height: 4px;
      background: var(--cairn-primary-color, #3b82f6);
      border-radius: 2px;
    }

    /* Section drop zone for cross-list drag */
    .section-drop-zone {
      height: 0;
      transition: height 0.15s;
      border-radius: 4px;
    }

    .section-drop-zone.drag-over {
      height: 4px;
      background: var(--cairn-primary-color, #3b82f6);
    }

    /* Ungrouped items get their own rounded corners */
    .gear-group:not(.has-header) .gear-item-wrapper:first-of-type {
      border-radius: 8px 8px 0 0;
    }

    .gear-group .gear-item-wrapper:last-of-type {
      border-radius: 0 0 8px 8px;
    }

    .gear-group:not(.has-header) .gear-item-wrapper:only-of-type {
      border-radius: 8px;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    /* Grouped items connect to the header */
    .gear-group.has-header .gear-item-wrapper:last-of-type {
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: 0 0 8px 8px;
    }

    /* Item controls row — matches time-entry-controls */
    .gear-item {
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-sm, 8px);
      padding: var(--cairn-spacing-sm, 8px) var(--cairn-spacing-md, 16px);
    }

    .drag-handle {
      font-size: 20px;
      color: var(--cairn-text-secondary-color, #64748b);
      flex-shrink: 0;
      cursor: grab;
      opacity: 0.5;
    }

    .drag-handle:hover {
      opacity: 1;
    }

    .gear-item-fields {
      flex: 1;
      min-width: 0;
      display: flex;
      gap: var(--cairn-spacing-sm, 8px);
      align-items: center;
    }

    .gear-item-name {
      flex: 1;
      min-width: 0;
      --md-outlined-text-field-container-shape: 6px;
    }

    .gear-item-qty {
      width: 70px;
      flex-shrink: 0;
      --md-outlined-text-field-container-shape: 6px;
    }

    .gear-item-actions {
      display: flex;
      flex-shrink: 0;
      gap: 0;
    }

    @media (max-width: 768px) {
      .gear-item-fields {
        flex-direction: column;
      }

      .gear-item-name,
      .gear-item-qty {
        width: 100%;
        flex-shrink: 1;
      }

      .gear-item-actions {
        flex-direction: column;
      }
    }

    .gear-item-delete {
      flex-shrink: 0;
      --md-icon-button-icon-color: var(--cairn-text-secondary-color, #64748b);
    }

    .gear-note-button {
      flex-shrink: 0;
      --md-icon-button-icon-color: var(--cairn-text-secondary-color, #64748b);
    }

    /* Note preview row — matches time-note-row */
    .gear-note-row {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 0 var(--cairn-spacing-md, 16px) var(--cairn-spacing-sm, 8px) 40px;
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
      padding-top: var(--cairn-spacing-sm, 8px);
    }

    .gear-note-preview {
      flex: 1;
      font-size: 13px;
      color: var(--cairn-text-secondary-color, #64748b);
      white-space: pre-wrap;
      line-height: 1.4;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .gear-note-row .material-icons {
      font-size: 14px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .gear-note-row md-icon-button {
      --md-icon-button-icon-size: 16px;
      --md-icon-button-state-layer-height: 24px;
      --md-icon-button-state-layer-width: 24px;
      height: 24px;
      width: 24px;
    }

    .gear-note-row md-icon-button .material-icons {
      font-size: 14px;
    }

    /* Note edit row — matches time-note-edit */
    .gear-note-edit {
      padding: 0 var(--cairn-spacing-md, 16px) var(--cairn-spacing-sm, 8px) 40px;
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
      padding-top: var(--cairn-spacing-sm, 8px);
    }

    .gear-note-edit md-outlined-text-field {
      width: 100%;
    }

    /* Quick-add field */
    .quick-add {
      padding: var(--cairn-spacing-sm, 8px) var(--cairn-spacing-md, 16px);
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-top: none;
      border-radius: 0 0 8px 8px;
    }

    .gear-group.has-header .quick-add {
      border-radius: 0 0 8px 8px;
    }

    .quick-add md-outlined-text-field {
      width: 100%;
      --md-outlined-text-field-container-shape: 6px;
    }

    /* Section actions */
    .section-actions {
      display: flex;
      gap: var(--cairn-spacing-sm, 8px);
      margin-top: var(--cairn-spacing-sm, 8px);
      margin-bottom: var(--cairn-spacing-lg, 24px);
    }

    /* Add group inline input */
    .add-group-input {
      margin-top: var(--cairn-spacing-sm, 8px);
      margin-bottom: var(--cairn-spacing-sm, 8px);
    }

    .add-group-input md-outlined-text-field {
      width: 100%;
    }

    /* Template picker */
    .template-picker {
      margin-bottom: var(--cairn-spacing-sm, 8px);
    }

    .template-picker-title {
      font-size: 14px;
      font-weight: 600;
      margin-bottom: var(--cairn-spacing-sm, 8px);
    }

    .template-card {
      position: relative;
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      padding: var(--cairn-spacing-sm, 8px) var(--cairn-spacing-md, 16px);
      margin-bottom: var(--cairn-spacing-sm, 8px);
      cursor: pointer;
      transition: border-color 0.15s;
    }

    .template-card:hover {
      border-color: var(--cairn-primary-color, #3b82f6);
    }

    .template-card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .template-name {
      font-size: 14px;
      font-weight: 500;
    }

    .template-delete {
      --md-icon-button-icon-color: var(--cairn-text-secondary-color, #64748b);
    }

    .template-desc {
      font-size: 13px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    /* Material Icons */
    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      line-height: 1;
      letter-spacing: normal;
      text-transform: none;
      display: inline-block;
      white-space: nowrap;
      word-wrap: normal;
      direction: ltr;
      user-select: none;
    }
  `

  @property({ type: Array }) teamGear: PlanPackItem[] = []
  @property({ type: Array }) individualGear: PlanPackItem[] = []
  @property({ type: Array }) memberIds: string[] = []

  @state() private noteEditItemId: string | null = null
  @state() private showTemplatePickerFor: 'team' | 'individual' | null = null
  @state() private addGroupFor: 'team' | 'individual' | null = null
  @state() private pendingGroups: Map<string, string[]> = new Map()

  // Saved templates from IndexedDB (with their pack list ID for deletion)
  @state() private savedTemplates: (PackTemplateData & { packListId: string })[] = []
  @state() private savedGroupMessage: string | null = null

  // Drag state
  private dragSource: { list: 'team' | 'individual'; id: string } | null = null
  @state() private dragOverItemId: string | null = null
  @state() private dragOverZone: string | null = null

  override connectedCallback(): void {
    super.connectedCallback()
    void this.loadSavedTemplates()
  }

  private async loadSavedTemplates(): Promise<void> {
    try {
      const result = await packListStorage.getPackListsForUser(authService.getState().userId, {
        isTemplate: true,
      })
      if (!result.success || !result.data) return

      const templates: (PackTemplateData & { packListId: string })[] = []
      for (const pl of result.data) {
        const itemsResult = await packItemStorage.getItemsForPackList(pl.id)
        if (!itemsResult.success || !itemsResult.data) continue
        const templateItems: PackTemplateData['items'] = itemsResult.data.map(i => {
          const entry: PackTemplateData['items'][number] = { name: i.name }
          if (i.description) entry.description = i.description
          if (i.quantity > 1) entry.quantity = i.quantity
          return entry
        })
        templates.push({
          packListId: pl.id,
          name: pl.name,
          description: pl.description,
          items: templateItems,
        })
      }
      this.savedTemplates = templates
    } catch {
      // Storage may not be available (e.g. during tests)
    }
  }

  private async saveGroupAsTemplate(list: 'team' | 'individual', groupName: string): Promise<void> {
    const items = (list === 'team' ? this.teamGear : this.individualGear).filter(
      i => i.group === groupName
    )
    if (items.length === 0) return

    const plResult = await packListStorage.createPackList({
      name: groupName,
      description: `Saved from plan`,
      category: 'General',
      isTemplate: true,
      createdBy: authService.getState().userId,
      isPublic: false,
    })
    if (!plResult.success || !plResult.data) return

    for (const item of items) {
      await packItemStorage.createPackItem({
        packListId: plResult.data.id,
        name: item.name,
        description: item.description,
        category: 'General',
        isEssential: false,
        quantity: item.quantity ?? 1,
      })
    }

    await this.loadSavedTemplates()
    this.savedGroupMessage = groupName
    setTimeout(() => {
      this.savedGroupMessage = null
    }, 2000)
  }

  private async deleteTemplate(packListId: string): Promise<void> {
    try {
      await packListStorage.deleteById(packListId)
      await this.loadSavedTemplates()
    } catch {
      // ignore
    }
  }

  private fireGearChanged(
    teamGear: PlanPackItem[] = this.teamGear,
    individualGear: PlanPackItem[] = this.individualGear
  ): void {
    this.dispatchEvent(
      new CustomEvent('gear-changed', {
        detail: { teamGear, individualGear },
        bubbles: true,
        composed: true,
      })
    )
  }

  // --- Item operations ---

  private addItem(list: 'team' | 'individual', name: string, group?: string): void {
    if (!name.trim()) return
    const newItem: PlanPackItem = {
      id: crypto.randomUUID(),
      name: name.trim(),
      packed: false,
      ...(group ? { group } : {}),
    }
    if (list === 'team') {
      this.fireGearChanged([...this.teamGear, newItem], this.individualGear)
    } else {
      this.fireGearChanged(this.teamGear, [...this.individualGear, newItem])
    }
    // Remove from pending if this was the first item in a pending group
    if (group) {
      const pending = this.pendingGroups.get(list)
      if (pending?.includes(group)) {
        const next = new Map(this.pendingGroups)
        next.set(
          list,
          pending.filter(g => g !== group)
        )
        this.pendingGroups = next
      }
    }
  }

  private removeItem(list: 'team' | 'individual', id: string): void {
    if (list === 'team') {
      this.fireGearChanged(
        this.teamGear.filter(i => i.id !== id),
        this.individualGear
      )
    } else {
      this.fireGearChanged(
        this.teamGear,
        this.individualGear.filter(i => i.id !== id)
      )
    }
  }

  private updateItem(list: 'team' | 'individual', id: string, field: string, value: unknown): void {
    const updateList = (items: PlanPackItem[]): PlanPackItem[] =>
      items.map(i => (i.id === id ? { ...i, [field]: value } : i))

    if (list === 'team') {
      this.fireGearChanged(updateList(this.teamGear), this.individualGear)
    } else {
      this.fireGearChanged(this.teamGear, updateList(this.individualGear))
    }
  }

  private handleQuickAdd(
    list: 'team' | 'individual',
    group: string | null,
    e: KeyboardEvent
  ): void {
    if (e.key !== 'Enter') return
    const input = e.target as HTMLInputElement
    this.addItem(list, input.value, group ?? undefined)
    input.value = ''
  }

  private toggleNote(itemId: string): void {
    this.noteEditItemId = this.noteEditItemId === itemId ? null : itemId
  }

  // --- Group operations ---

  private removeGroup(list: 'team' | 'individual', groupName: string): void {
    // Remove from pending groups (empty groups)
    const pending = this.pendingGroups.get(list)
    if (pending?.includes(groupName)) {
      const next = new Map(this.pendingGroups)
      next.set(
        list,
        pending.filter(g => g !== groupName)
      )
      this.pendingGroups = next
    }

    // Remove items with this group from data
    const removeFromGroup = (items: PlanPackItem[]): PlanPackItem[] =>
      items.filter(i => i.group !== groupName)

    if (list === 'team') {
      this.fireGearChanged(removeFromGroup(this.teamGear), this.individualGear)
    } else {
      this.fireGearChanged(this.teamGear, removeFromGroup(this.individualGear))
    }
  }

  private handleAddGroup(list: 'team' | 'individual', e: KeyboardEvent): void {
    if (e.key !== 'Enter') return
    const input = e.target as HTMLInputElement
    const name = input.value.trim()
    if (!name) return

    const next = new Map(this.pendingGroups)
    const current = next.get(list) ?? []
    if (!current.includes(name)) {
      next.set(list, [...current, name])
    }
    this.pendingGroups = next
    this.addGroupFor = null
    input.value = ''
  }

  // --- Drag-and-drop ---

  private enableDrag(e: MouseEvent, list: 'team' | 'individual', id: string): void {
    const wrapper = (e.target as HTMLElement).closest('.gear-item-wrapper') as HTMLElement
    wrapper.draggable = true
    wrapper.addEventListener(
      'dragstart',
      (de: DragEvent) => {
        de.dataTransfer?.setData('text/plain', id)
        this.dragSource = { list, id }
      },
      { once: true }
    )
  }

  private disableDrag(e: MouseEvent): void {
    const wrapper = (e.target as HTMLElement).closest('.gear-item-wrapper') as HTMLElement
    wrapper.draggable = false
  }

  private handleItemDragOver(e: DragEvent, itemId: string): void {
    e.preventDefault()
    this.dragOverItemId = itemId
    this.dragOverZone = null
  }

  private handleZoneDragOver(e: DragEvent, zoneId: string): void {
    e.preventDefault()
    this.dragOverZone = zoneId
    this.dragOverItemId = null
  }

  private handleDragEnd(e: DragEvent): void {
    const wrapper = (e.target as HTMLElement).closest<HTMLElement>('.gear-item-wrapper')
    if (wrapper) wrapper.draggable = false
    this.resetDrag()
  }

  private resetDrag(): void {
    this.dragSource = null
    this.dragOverItemId = null
    this.dragOverZone = null
  }

  /**
   * Drop onto an item — insert before that item, adopting its list and group.
   */
  private handleDropOnItem(targetList: 'team' | 'individual', targetItemId: string): void {
    if (!this.dragSource) return
    const { list: srcList, id: srcId } = this.dragSource
    if (srcId === targetItemId) {
      this.resetDrag()
      return
    }

    // Find the dragged item
    const srcItems = srcList === 'team' ? this.teamGear : this.individualGear
    const item = srcItems.find(i => i.id === srcId)
    if (!item) {
      this.resetDrag()
      return
    }

    // Find the target item to get its group
    const targetItems = targetList === 'team' ? this.teamGear : this.individualGear
    const targetItem = targetItems.find(i => i.id === targetItemId)
    const newGroup = targetItem?.group

    // Build new arrays
    let newTeam = [...this.teamGear]
    let newIndividual = [...this.individualGear]

    // Remove from source
    if (srcList === 'team') {
      newTeam = newTeam.filter(i => i.id !== srcId)
    } else {
      newIndividual = newIndividual.filter(i => i.id !== srcId)
    }

    // Insert before target in destination
    const movedItem = { ...item, group: newGroup }
    const dest = targetList === 'team' ? newTeam : newIndividual
    const targetIdx = dest.findIndex(i => i.id === targetItemId)
    dest.splice(targetIdx, 0, movedItem)

    this.fireGearChanged(newTeam, newIndividual)
    this.resetDrag()
  }

  /**
   * Drop onto a zone — append to that zone's list/group.
   * Zone IDs follow the pattern: "list:groupName" or "list:" for ungrouped.
   */
  private handleDropOnZone(zoneId: string): void {
    if (!this.dragSource) return
    const { list: srcList, id: srcId } = this.dragSource

    const sepIdx = zoneId.indexOf(':')
    const targetList = zoneId.slice(0, sepIdx) as 'team' | 'individual'
    const groupName = zoneId.slice(sepIdx + 1) || undefined

    const srcItems = srcList === 'team' ? this.teamGear : this.individualGear
    const item = srcItems.find(i => i.id === srcId)
    if (!item) {
      this.resetDrag()
      return
    }

    let newTeam = [...this.teamGear]
    let newIndividual = [...this.individualGear]

    // Remove from source
    if (srcList === 'team') {
      newTeam = newTeam.filter(i => i.id !== srcId)
    } else {
      newIndividual = newIndividual.filter(i => i.id !== srcId)
    }

    // Append to destination with new group
    const movedItem = { ...item, group: groupName }
    if (targetList === 'team') {
      newTeam.push(movedItem)
    } else {
      newIndividual.push(movedItem)
    }

    this.fireGearChanged(newTeam, newIndividual)
    this.resetDrag()
  }

  // --- Templates ---

  private addTemplateItems(template: PackTemplateData, list: 'team' | 'individual'): void {
    const newItems: PlanPackItem[] = template.items.map(item => ({
      id: crypto.randomUUID(),
      name: item.name,
      description: item.description,
      quantity: item.quantity,
      packed: false,
      group: template.name,
    }))

    if (list === 'team') {
      this.fireGearChanged([...this.teamGear, ...newItems], this.individualGear)
    } else {
      this.fireGearChanged(this.teamGear, [...this.individualGear, ...newItems])
    }
    this.showTemplatePickerFor = null
  }

  // --- Rendering ---

  private renderItem(item: PlanPackItem, list: 'team' | 'individual'): unknown {
    const hasNote = Boolean(item.description)
    const editingNote = this.noteEditItemId === item.id

    const isDragOver = this.dragOverItemId === item.id
    const isDragging = this.dragSource?.id === item.id

    return html`
      <div
        class="gear-item-wrapper ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}"
        @dragover=${(e: DragEvent) => this.handleItemDragOver(e, item.id)}
        @drop=${() => this.handleDropOnItem(list, item.id)}
        @dragend=${(e: DragEvent) => this.handleDragEnd(e)}
      >
        <div class="gear-item">
          <span
            class="material-icons drag-handle"
            @mousedown=${(e: MouseEvent) => this.enableDrag(e, list, item.id)}
            @mouseup=${(e: MouseEvent) => this.disableDrag(e)}
            >drag_indicator</span
          >
          <div class="gear-item-fields">
            <md-outlined-text-field
              class="gear-item-name"
              label="Name"
              .value=${item.name}
              @input=${(e: Event) =>
                this.updateItem(list, item.id, 'name', (e.target as HTMLInputElement).value)}
            ></md-outlined-text-field>
            <md-outlined-text-field
              class="gear-item-qty"
              type="number"
              label="Qty"
              min="1"
              .value=${String(item.quantity ?? 1)}
              @input=${(e: Event) => {
                const val = parseInt((e.target as HTMLInputElement).value)
                this.updateItem(list, item.id, 'quantity', val > 1 ? val : undefined)
              }}
            ></md-outlined-text-field>
          </div>
          <div class="gear-item-actions">
            <md-icon-button
              class="gear-note-button"
              @click=${() => this.toggleNote(item.id)}
              aria-label="${hasNote ? 'Edit note' : 'Add note'}"
              title="${hasNote ? 'Edit note' : 'Add note'}"
            >
              <span class="material-icons">${hasNote ? 'sticky_note_2' : 'note_add'}</span>
            </md-icon-button>
            <md-icon-button
              class="gear-item-delete"
              @click=${() => this.removeItem(list, item.id)}
              aria-label="Remove item"
            >
              <span class="material-icons">close</span>
            </md-icon-button>
          </div>
        </div>
        ${hasNote && !editingNote
          ? html`
              <div class="gear-note-row">
                <span class="material-icons">sticky_note_2</span>
                <span class="gear-note-preview">${item.description}</span>
              </div>
            `
          : nothing}
        ${editingNote
          ? html`
              <div class="gear-note-edit">
                <md-outlined-text-field
                  type="textarea"
                  label="Notes"
                  .value=${item.description ?? ''}
                  @input=${(e: Event) =>
                    this.updateItem(
                      list,
                      item.id,
                      'description',
                      (e.target as HTMLInputElement).value || undefined
                    )}
                  rows="3"
                ></md-outlined-text-field>
              </div>
            `
          : nothing}
      </div>
    `
  }

  private renderGroupBlock(group: PackGroup, list: 'team' | 'individual'): unknown {
    const hasHeader = group.name !== null
    const zoneId = `${list}:${group.name ?? ''}`
    const isZoneDragOver = this.dragOverZone === zoneId

    return html`
      <div class="gear-group ${hasHeader ? 'has-header' : ''}">
        ${hasHeader
          ? html`
              <div class="gear-group-header">
                <span class="gear-group-name">${group.name}</span>
                ${this.savedGroupMessage === group.name
                  ? html`<span class="gear-group-saved">Saved!</span>`
                  : nothing}
                <md-icon-button
                  class="gear-group-save"
                  @click=${(e: Event) => {
                    e.stopPropagation()
                    void this.saveGroupAsTemplate(list, group.name!)
                  }}
                  aria-label="Save as template"
                  title="Save as template"
                >
                  <span class="material-icons">bookmark_add</span>
                </md-icon-button>
                <md-icon-button
                  class="gear-group-remove"
                  @click=${(e: Event) => {
                    e.stopPropagation()
                    this.removeGroup(list, group.name!)
                  }}
                  aria-label="Remove group"
                >
                  <span class="material-icons">close</span>
                </md-icon-button>
              </div>
            `
          : nothing}
        ${group.items.map(item => this.renderItem(item, list))}
        <div
          class="gear-group-drop-zone ${isZoneDragOver ? 'drag-over' : ''}"
          @dragover=${(e: DragEvent) => this.handleZoneDragOver(e, zoneId)}
          @drop=${() => this.handleDropOnZone(zoneId)}
        ></div>
        <div class="quick-add">
          <md-outlined-text-field
            class="quick-add-input"
            label="Add item..."
            @keydown=${(e: KeyboardEvent) => this.handleQuickAdd(list, group.name, e)}
          ></md-outlined-text-field>
        </div>
      </div>
    `
  }

  private renderTemplatePicker(list: 'team' | 'individual'): unknown {
    if (this.showTemplatePickerFor !== list) return nothing

    return html`
      <div class="template-picker">
        <div class="template-picker-title">Add from Template</div>
        ${DEFAULT_PACK_TEMPLATES.map(
          t => html`
            <div class="template-card" @click=${() => this.addTemplateItems(t, list)}>
              <div class="template-name">${t.name}</div>
              <div class="template-desc">${t.description} (${t.items.length} items)</div>
            </div>
          `
        )}
        ${this.savedTemplates.map(
          t => html`
            <div class="template-card" @click=${() => this.addTemplateItems(t, list)}>
              <div class="template-card-header">
                <div class="template-name">${t.name}</div>
                <md-icon-button
                  class="template-delete"
                  @click=${(e: Event) => {
                    e.stopPropagation()
                    void this.deleteTemplate(t.packListId)
                  }}
                  aria-label="Delete template"
                >
                  <span class="material-icons">close</span>
                </md-icon-button>
              </div>
              <div class="template-desc">${t.description} (${t.items.length} items)</div>
            </div>
          `
        )}
        ${DEFAULT_PACK_TEMPLATES.length === 0 && this.savedTemplates.length === 0
          ? html`<div class="template-desc">No templates available</div>`
          : nothing}
      </div>
    `
  }

  private renderGearSection(
    label: string,
    items: PlanPackItem[],
    list: 'team' | 'individual',
    sectionClass: string
  ): unknown {
    const groups = groupPackItems(items)

    // Add pending (empty) groups
    const pending = this.pendingGroups.get(list) ?? []
    const existingGroupNames = new Set(groups.filter(g => g.name !== null).map(g => g.name))
    for (const name of pending) {
      if (!existingGroupNames.has(name)) {
        // Insert before ungrouped
        const ungroupedIndex = groups.findIndex(g => g.name === null)
        const emptyGroup: PackGroup = { name, items: [] }
        if (ungroupedIndex >= 0) {
          groups.splice(ungroupedIndex, 0, emptyGroup)
        } else {
          groups.push(emptyGroup)
        }
      }
    }

    const sectionZoneId = `${list}:`
    const isSectionDragOver = this.dragOverZone === sectionZoneId && groups.length === 0

    return html`
      <div class="${sectionClass}">
        <div class="subsection-label">${label}</div>
        ${groups.map(group => this.renderGroupBlock(group, list))}
        <div
          class="section-drop-zone ${isSectionDragOver ? 'drag-over' : ''}"
          @dragover=${(e: DragEvent) => this.handleZoneDragOver(e, sectionZoneId)}
          @drop=${() => this.handleDropOnZone(sectionZoneId)}
        ></div>
        ${groups.length === 0
          ? html`
              <div class="gear-group">
                <div
                  class="quick-add"
                  style="border: 1px solid var(--cairn-border-color, #e2e8f0); border-radius: 8px;"
                >
                  <md-outlined-text-field
                    class="quick-add-input"
                    label="Add item..."
                    @keydown=${(e: KeyboardEvent) => this.handleQuickAdd(list, null, e)}
                  ></md-outlined-text-field>
                </div>
              </div>
            `
          : nothing}
        ${this.addGroupFor === list
          ? html`
              <div class="add-group-input">
                <md-outlined-text-field
                  label="Group name"
                  @keydown=${(e: KeyboardEvent) => this.handleAddGroup(list, e)}
                ></md-outlined-text-field>
              </div>
            `
          : nothing}
        <div class="section-actions">
          <md-outlined-button
            class="add-template-btn"
            @click=${() => {
              this.showTemplatePickerFor = this.showTemplatePickerFor === list ? null : list
            }}
          >
            <span class="material-icons" slot="icon">playlist_add</span>
            Add from Template
          </md-outlined-button>
          <md-outlined-button
            class="add-group-btn"
            @click=${() => {
              this.addGroupFor = this.addGroupFor === list ? null : list
            }}
          >
            <span class="material-icons" slot="icon">create_new_folder</span>
            Add Group
          </md-outlined-button>
        </div>
        ${this.renderTemplatePicker(list)}
      </div>
    `
  }

  override render(): unknown {
    return html`
      ${this.renderGearSection('Team Gear', this.teamGear, 'team', 'team-gear-section')}
      ${this.renderGearSection(
        'Individual Gear',
        this.individualGear,
        'individual',
        'individual-gear-section'
      )}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'plan-pack-editor': PlanPackEditor
  }
}
